import { useState } from "react";
import { motion } from "framer-motion";
import { X, Loader2, ShieldCheck, ArrowRight, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { formatFCFA } from "@/lib/user";

/** Numéro Mobile Money ivoirien : 10 chiffres (07…, 05…, 01…) ou format international. */
export const numeroValide = (valeur) => {
  const chiffres = String(valeur || "").replace(/\D/g, "");
  return chiffres.length === 10 || (chiffres.startsWith("225") && chiffres.length === 13);
};

const OPERATEURS = [
  { nom: "Orange Money", couleur: "#FF7900", initiales: "OM" },
  { nom: "MTN MoMo", couleur: "#FFCC00", initiales: "MTN", texteSombre: true },
  { nom: "Moov Money", couleur: "#0057B8", initiales: "Moov" },
  { nom: "Wave", couleur: "#1DC8FF", initiales: "Wave", texteSombre: true },
];

/** Choix de l'opérateur puis redirection vers la page de paiement CinetPay. */
export const ChoixOperateurModal = ({ offre, enCours, numeroInitial, onChoisir, onClose }) => {
  const [numero, setNumero] = useState(String(numeroInitial || "").replace(/\D/g, "").slice(-10));
  // « moi » = je paie avec mon numéro ; « proche » = un tiers paie pour moi.
  const [qui, setQui] = useState("moi");
  const [numeroProche, setNumeroProche] = useState("");
  const [nomProche, setNomProche] = useState("");
  const tiers = qui === "proche";
  const numeroUtilise = tiers ? numeroProche : numero;

  const choisir = (nom) => {
    if (!numeroValide(numeroUtilise)) {
      toast.error(tiers ? "Numéro du proche invalide" : "Numéro Mobile Money invalide", {
        description: "Saisissez les 10 chiffres, ex. 07 07 07 07 07.",
      });
      return;
    }
    onChoisir(nom, numeroUtilise, { payeur_tiers: tiers, payeur_nom: nomProche.trim() || null });
  };

  return (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
    data-testid="choix-operateur-modal"
  >
    <motion.div
      initial={{ y: 60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 40, opacity: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 26 }}
      className="bg-white w-full sm:max-w-md rounded-t-[28px] sm:rounded-[28px] p-6 sm:p-8 relative"
    >
      <button
        onClick={onClose}
        data-testid="choix-operateur-fermer"
        className="absolute top-5 right-5 text-stone-400 hover:text-stone-800 transition-colors"
        aria-label="Fermer"
      >
        <X size={20} />
      </button>

      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400">Paiement sécurisé</p>
      <h2 className="font-heading text-2xl font-extrabold text-stone-900 mt-2">
        {formatFCFA(offre.montant)} à payer
      </h2>
      <p className="text-sm text-stone-600 mt-2 leading-relaxed">
        Choisissez qui paie, puis l'opérateur. Dès la confirmation de l'opérateur, l'accès
        s'ouvre automatiquement sur votre compte.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="choix-payeur">
        {[["moi", "Payer avec mon numéro", "Je règle moi-même"],
          ["proche", "Faire payer par un proche", "Il reçoit le lien par SMS"]].map(([cle, titre, aide]) => (
          <button key={cle} type="button" onClick={() => setQui(cle)}
            data-testid={`payeur-${cle}`}
            className={`rounded-2xl border-2 px-4 py-3 text-left transition-colors ${
              qui === cle ? "border-[#0A5C36] bg-[#0A5C36]/5" : "border-[#E5DFD5] hover:border-stone-400"}`}>
            <span className="block text-sm font-bold text-stone-900">{titre}</span>
            <span className="text-[11px] text-stone-500">{aide}</span>
          </button>
        ))}
      </div>

      {tiers && (
        <div className="mt-4 space-y-3 rounded-2xl bg-[#FAF8F5] p-4" data-testid="bloc-payeur-proche">
          <p className="text-xs font-semibold text-stone-600">
            L'abonnement s'activera sur <b>votre compte</b>, même si c'est votre proche qui paie.
          </p>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-stone-500">
              Nom du proche (facultatif)
            </span>
            <input value={nomProche} onChange={(e) => setNomProche(e.target.value)}
              placeholder="Ex. Grand frère Koffi" data-testid="nom-proche-input"
              className="mt-2 w-full rounded-2xl border-2 border-[#E5DFD5] px-4 py-2.5 text-sm font-semibold text-stone-900 outline-none focus:border-stone-900" />
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-stone-500">
              Numéro Mobile Money du proche
            </span>
            <span className="mt-2 flex items-center gap-2 rounded-2xl border-2 border-[#E5DFD5] px-4 py-3 transition-colors focus-within:border-stone-900">
              <Smartphone size={16} className="shrink-0 text-stone-400" />
              <span className="text-sm font-semibold text-stone-500">+225</span>
              <input value={numeroProche} inputMode="numeric" placeholder="07 07 07 07 07"
                onChange={(e) => setNumeroProche(e.target.value.replace(/\D/g, "").slice(0, 10))}
                data-testid="numero-proche-input"
                className="min-w-0 flex-1 bg-transparent text-sm font-semibold tracking-wide text-stone-900 outline-none placeholder:font-normal placeholder:text-stone-300" />
            </span>
          </label>
        </div>
      )}

      <label className={`block mt-6 ${tiers ? "hidden" : ""}`}>
        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-stone-500">
          Numéro Mobile Money à débiter
        </span>
        <span className="mt-2 flex items-center gap-2 rounded-2xl border-2 border-[#E5DFD5] focus-within:border-stone-900 px-4 py-3 transition-colors">
          <Smartphone size={16} className="text-stone-400 shrink-0" />
          <span className="text-sm font-semibold text-stone-500">+225</span>
          <input
            value={numero}
            onChange={(e) => setNumero(e.target.value.replace(/\D/g, "").slice(0, 10))}
            inputMode="numeric"
            placeholder="07 07 07 07 07"
            data-testid="numero-mobile-money-input"
            className="flex-1 min-w-0 bg-transparent outline-none text-sm font-semibold text-stone-900 tracking-wide placeholder:text-stone-300 placeholder:font-normal"
          />
        </span>
      </label>

      <div className="grid grid-cols-2 gap-3 mt-6">
        {OPERATEURS.map((op) => (
          <button
            key={op.nom}
            disabled={!!enCours}
            onClick={() => choisir(op.nom)}
            data-testid={`operateur-${op.nom.split(" ")[0].toLowerCase()}`}
            className="group flex items-center gap-3 rounded-2xl border-2 border-[#E5DFD5] hover:border-stone-900 p-3 text-left transition-colors disabled:opacity-50"
          >
            <span
              className="w-11 h-11 rounded-xl grid place-items-center text-[11px] font-extrabold shrink-0"
              style={{ backgroundColor: op.couleur, color: op.texteSombre ? "#12100E" : "#fff" }}
            >
              {op.initiales}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-bold text-stone-900 leading-tight">{op.nom}</span>
              <span className="text-[11px] text-stone-500 inline-flex items-center gap-1">
                {enCours === op.nom ? (
                  <>
                    <Loader2 size={11} className="animate-spin" /> Ouverture…
                  </>
                ) : (
                  <>
                    Payer <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
                  </>
                )}
              </span>
            </span>
          </button>
        ))}
      </div>

      <p className="text-[11px] text-stone-500 mt-6 flex items-start gap-2 leading-relaxed">
        <ShieldCheck size={14} className="shrink-0 mt-0.5 text-[#0A5C36]" />
        Transaction traitée par CinetPay. Aucun code à recopier : dès la confirmation de l'opérateur, votre
        abonnement s'active tout seul.
      </p>
    </motion.div>
  </motion.div>
  );
};
